import { useState, useEffect, useCallback } from "react";
import { localStorageManager } from "@/services/localStorage";
import { LOCAL_STORAGE_BASE_KEYS } from "@/types/v2";
import {
  GroupingConfig,
  GroupByOption,
  DEFAULT_GROUPING_CONFIG,
} from "../types";

export interface UsePersistentGroupingProps {
  shopId?: string;
  defaultConfig?: Partial<GroupingConfig>;
}

export interface UsePersistentGroupingReturn {
  groupingConfig: GroupingConfig;
  setGroupBy: (groupBy: GroupByOption) => void;
  toggleGroupStatistics: () => void;
  toggleGroupCounts: () => void;
  toggleGroupCollapse: (groupId: string) => void;
  resetGrouping: () => void;
  isGroupCollapsed: (groupId: string) => boolean;
}

export const usePersistentGrouping = ({
  shopId,
  defaultConfig = {},
}: UsePersistentGroupingProps): UsePersistentGroupingReturn => {
  // Create storage key specific to shop
  const storageKey = LOCAL_STORAGE_BASE_KEYS.DASHBOARD_LIST_GROUPING;

  // Initialize state with localStorage or defaults
  const [groupingConfig, setGroupingConfig] = useState<GroupingConfig>(() => {
    try {
      const stored = localStorageManager.getItem(storageKey, shopId);
      if (stored) {
        const parsed = JSON.parse(stored) as GroupingConfig;
        return {
          ...DEFAULT_GROUPING_CONFIG,
          ...defaultConfig,
          ...parsed,
        };
      }
    } catch (error) {
      console.warn("Failed to load grouping config from localStorage:", error);
    }

    return {
      ...DEFAULT_GROUPING_CONFIG,
      ...defaultConfig,
    };
  });

  // Save to localStorage whenever config changes
  useEffect(() => {
    try {
      localStorageManager.setItem(
        storageKey,
        JSON.stringify(groupingConfig),
        shopId
      );
    } catch (error) {
      console.warn("Failed to save grouping config to localStorage:", error);
    }
  }, [groupingConfig, storageKey, shopId]);

  // Update group by option
  const setGroupBy = useCallback((groupBy: GroupByOption) => {
    setGroupingConfig((prev) => ({
      ...prev,
      groupBy,
      // Clear collapsed groups when changing grouping method
      collapsedGroups: groupBy === "none" ? [] : prev.collapsedGroups,
    }));
  }, []);

  // Toggle statistics visibility
  const toggleGroupStatistics = useCallback(() => {
    setGroupingConfig((prev) => ({
      ...prev,
      showStatistics: !prev.showStatistics,
    }));
  }, []);

  // Toggle counts visibility
  const toggleGroupCounts = useCallback(() => {
    setGroupingConfig((prev) => ({
      ...prev,
      showCounts: !prev.showCounts,
    }));
  }, []);

  // Toggle group collapse state
  const toggleGroupCollapse = useCallback((groupId: string) => {
    setGroupingConfig((prev) => {
      const { collapsedGroups } = prev;
      const isCurrentlyCollapsed = collapsedGroups.includes(groupId);

      return {
        ...prev,
        collapsedGroups: isCurrentlyCollapsed
          ? collapsedGroups.filter((id) => id !== groupId)
          : [...collapsedGroups, groupId],
      };
    });
  }, []);

  // Reset to default configuration
  const resetGrouping = useCallback(() => {
    const newConfig = {
      ...DEFAULT_GROUPING_CONFIG,
      ...defaultConfig,
    };
    setGroupingConfig(newConfig);
  }, [defaultConfig]);

  // Check if a group is collapsed
  const isGroupCollapsed = useCallback(
    (groupId: string): boolean => {
      return groupingConfig.collapsedGroups.includes(groupId);
    },
    [groupingConfig.collapsedGroups]
  );

  return {
    groupingConfig,
    setGroupBy,
    toggleGroupStatistics,
    toggleGroupCounts,
    toggleGroupCollapse,
    resetGrouping,
    isGroupCollapsed,
  };
};
